import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { HasOnComplete } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { OrderConfirmationPageContext } from "@insite/content-library/Pages/OrderConfirmationPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Link, { StyledIcon } from "@insite/mobius/Link";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import { addCustomAttribute } from "@MasterBluePrint/Handlers/Common/NoibuLibrary";
import checkNew from "@MasterBluePrint/Icons/checkNew";
import orderHistoryNew from "@MasterBluePrint/Icons/orderHistoryNew";
import React, { FC, useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    cartState: getCartState(state, state.pages.orderConfirmation.cartId),
    settingsCollection: getSettingsCollection(state),
    homePageUrl: getHomePageUrl(state),
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat, // ! Check whether this one is necessary, or the other vat variable below
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

export interface OrderConfirmationHeaderStyles {
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    headerGridContainer?: GridContainerProps;
    narrowOverflowMenu?: OverflowMenuProps;
    title: TypographyProps;
    titleGridItem: GridItemProps;
    continueButton?: ButtonPresentationProps;
    printButton?: ButtonPresentationProps;
    addToListButton?: ButtonPresentationProps;
    buttonGridItem?: GridItemProps;
    printClickable?: ClickableProps;
    addToListClickable?: ClickableProps;
    continueClickable?: ClickableProps;
    printListModal?: TwoButtonModalStyles;
    orderNumberHeading?: TypographyProps;
    orderTitle?: InjectableCss;
    viewHistoryButton?: InjectableCss;
}

export const headerStyles: OrderConfirmationHeaderStyles = {
    continueButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            color: #002554;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: 0;
            line-height: 1.3125rem;
            text-transform: uppercase;
            width: 21.5rem;

            @media screen and (min-width: 768px) and (max-width: 990px) {
                width: 18.75rem;
            }

            @media (max-width: 768px) {
                width: 15rem;
            }

            @media print {
                display: none;
            }
        `,
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            border: none;
            background: none;
            padding: 0;
            color: #333F48;
            font-family: Lato;
            font-size: .875rem;
            letter-spacing: .01rem;
            line-height: 1.125rem;
            float: right;
            height: auto;
            span {
                display: inline-block;
                vertical-align: middle;
                margin-left: .31250625rem;
            }

            svg {
                display: inline-block;
                vertical-align: middle;
                font-size: .875rem;
                color: #333F48;
                width: 1.25rem;
                height: 1.25rem;
            }

            &:hover,
            &:active,
            &:focus {
                color: #333F48 !important;
                border: none !important;
                background: none !important;
                padding: 0;
            }

            @media print {
                display: none;
            }
        `,
    },
    addToListButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            margin-left: .625rem;

            @media print {
                display: none;
            }
        `,
    },
    buttonGridItem: {
        css: css`
            background: #f7f9fa;

            > div {
                width: 100%;
            }
        `,
        width: [12, 12, 12, 12, 12],
    },
    viewHistoryButton: {
        css: css`
            color: #333F48 !important;
            font-family: Lato;
            font-size: .875rem;
            letter-spacing: .01rem;
            line-height: 1.125rem;
            float: left;

            span {
                display: inline-block;
                vertical-align: middle;
                margin-left: .31250625rem;
                color: #333F48 !important;
                font-size: .875rem;
            }

            svg {
                display: inline-block;
                vertical-align: middle;
                font-size: .875rem;
                color: #333F48;
                width: 1.25rem;
                height: 1.25rem;
            }
            @media (max-width: 768px) {
                span, svg {
                    margin-left: 0;
                }
            }
            @media print {
                display: none;
            }
        `,
    },
    titleGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            justify-content: center;
            flex-wrap: wrap;
            border: 1px solid rgba(0,37,84,0.04);
            border-radius: 0 0 2px .125rem;
            background: linear-gradient(243.99deg, #2054A0 0%, #133C86 49.35%, #1F2D5E 100%);
            margin: .9375rem .9375rem 0 .9375rem !important;

            @media print {
                background: linear-gradient(243.99deg, #2054A0 0%, #133C86 49.35%, #1F2D5E 100%) !important;
                margin: 0 !important;
            }
        `,
    },
    title: {
        variant: "h2",
        as: "h1",
        css: css`
            margin: 0;
            color: #fff;
            font-size: 1.75rem;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 1.6875rem;
            text-align: center;
            padding: .75rem .9375rem .125rem .9375rem;
            flex-basis: 100%; 
            flex-wrap: wrap;
            display: flex;
            justify-content: center;
            @media screen and (max-width: 767px) {
                padding: .75rem 0 .125rem 0;
            }
            @media print {
                font-size: 1.75rem !important;
                font-weight: 500;
                letter-spacing: 0;
                line-height: 1.6875rem;
            }
        `,
    },
    orderTitle: {
        css: css`
            margin: 0;
            color: #fff;
            font-size: 1.5rem;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 1.4375rem;
            text-align: center;
            padding: .125rem .9375rem 0 .9375rem;
            flex-basis: 100%;
            flex-wrap: wrap;
            display: flex;
            justify-content: center;
            @media screen and (max-width: 767px) {
                padding: .125rem 0 0 0;
            }
            @media print {
                font-size: 1.5rem;
                font-weight: 500;
                letter-spacing: 0;
                line-height: 1.4375rem;
            }
        `,
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
    headerGridContainer: {
        css: css`
            text-align: center;
        `,
    }
};

type Props = HasHistory &
    WidgetProps &
    ReturnType<typeof mapStateToProps> & {
        setAddToListModalIsOpen: (parameter: {
            productInfos?: Omit<ProductInfo, "productDetailPath">[];
            modalIsOpen: boolean;
        }) => void;
        addToWishList: (parameter: AddToWishListParameter & HasOnComplete) => void;
    };

const styles = headerStyles;
const OrderConfirmationHeader: FC<Props> = props => {
    const { cartState } = props;
    const { history } = props;
    const { homePageUrl } = props;

    const OrderHistoryPage = props.myAccountPageLink?.children?.find(x => x.type === "OrderHistoryPage");
    const printOrOpenPrintAllModal = () => {
        openPrintDialog();
    };

    useEffect(() => {
        if (cartState?.value?.orderNumber) {
            addCustomAttribute("orderNumber", String(cartState.value.orderNumber));
        }
    }, [cartState?.value?.orderNumber]);


    if (!cartState.value) {
        return null;
    }

    const continueClickHandler = () => {
        history.push(homePageUrl);
    };

    const printLabel = translate("Print");
    const continueLabel = translate("Continue Shopping");
    const viewHistorytLabel = translate("View my order history");

    return (
        <GridContainer {...styles.headerGridContainer}>
            <GridItem {...styles.buttonGridItem}>
                <Typography as="div" className="button-container">
                    <Typography as="div" className="continue-container">
                        <Button className="continue-btn" {...styles.continueButton} onClick={continueClickHandler}>
                            {continueLabel}
                        </Button>
                    </Typography>
                    <Link href={OrderHistoryPage?.url} {...styles.viewHistoryButton}>
                        <Icon
                            src={orderHistoryNew}
                            css={css`
                                height: 1.25rem;
                                width: 1.25rem;
                                margin-right: .31250625rem;
                                > svg {
                                    height: 1.25rem;
                                    width: 1.25rem;
                                }
                            `}
                        />
                        <span> {viewHistorytLabel} </span>
                    </Link>
                    <Button {...styles.printButton} onClick={printOrOpenPrintAllModal}>
                        <Icon src="Printer" />
                        <span> {printLabel} </span>
                    </Button>
                </Typography>
            </GridItem>
            <GridItem {...styles.titleGridItem} className="title-container">
                <Icon
                    src={checkNew}
                    css={css`
                        height: 4.625rem;
                        width: 4.625rem;
                        margin-bottom: .31250625rem;
                        > svg {
                            height: 4.625rem;
                            width: 4.625rem;
                            fill: #fff;
                        }
                    `}
                />
                <Typography {...styles.title}>{siteMessage("OrderConfirmation_Success")}</Typography>
                <Typography as="h2" {...styles.orderTitle}>
                    {`${translate("Confirmation")} #:`} {props?.cartState?.value?.erpOrderNumber || props?.cartState?.value?.orderNumber}
                </Typography>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderConfirmationHeader)),
    definition: {
        displayName: "Page Header",
        allowedContexts: [OrderConfirmationPageContext],
        group: "Order Confirmation",
    },
};

export default widgetModule;
